import { useEffect, useMemo } from 'react'
import { useSelector } from 'react-redux'
import { arrangeColumns } from '@user/components/xGrid'
import {
  lockedOpportunityColumns,
  opportunityListColumns,
} from './opportunityListColumns'
import { useGetDivisionsOnMount } from '@user/store/divisions/hooks/useGetDivisionsOnMount'
import { useGetLocationTypesOnMount } from '@user/store/locationTypes/hooks/useGetLocationTypesOnMount'
import {
  useGetAllFunnelStagesOnMount,
  useGetSalesFunnelListOnMount,
} from '@user/store/salesFunnel/hooks'
import { useGetSalesTeamListOnMount } from '@user/store/salesTeam/hooks'
import { useGetSourcesOnMount } from '@user/store/sources/hooks/useGetSourcesOnMount'
import { useGetCampaignsOnMount } from '@user/store/campaigns/hooks/useGetCampaignsOnMount'
import { useGetBranchListOnMount } from '@user/store/branches/hooks/useGetBranchListOnMount'
import {
  selectOpportunityColumnOrder,
  useLoadListViewPreferencesOnce,
} from '@user/store/userPreferences'
import {
  useGetCustomFieldsOnMount,
  useSelectCustomFieldData,
} from '@user/store/customFields'
import {
  selectAllTargetPests,
  useGetTargetPestsOnMount,
} from '@user/store/targetPests'
import { useAddMissingColumns } from '@user/components/xGrid/valueFormatters/syncNewColumns'
import { useMapViewFeatureEnabled } from '@user/store/tenantFeatures/hooks/useMapViewFeatureEnabled'
import { useD2DKnockEnabled } from '@user/store/tenantFeatures/hooks/useD2DKnockEnabled'

export const useGetColumnData = () => {
  const divStatus = useGetDivisionsOnMount()
  const locTypeStatus = useGetLocationTypesOnMount()
  const funnelStatus = useGetSalesFunnelListOnMount()
  const stageStatus = useGetAllFunnelStagesOnMount()
  const teamStatus = useGetSalesTeamListOnMount()
  const sourceStatus = useGetSourcesOnMount()
  const campStatus = useGetCampaignsOnMount()
  const branchStatus = useGetBranchListOnMount()
  const userPrefsLoading = useLoadListViewPreferencesOnce()
  const customFieldsLoading = useGetCustomFieldsOnMount()
  const targetPests = useGetTargetPestsOnMount()
  const allTargetPests = useSelector(selectAllTargetPests)

  const { customFields } = useSelectCustomFieldData()

  const mapViewEnabled = useMapViewFeatureEnabled()
  const d2DKnockEnabled = useD2DKnockEnabled()
  const knockStatusEnabled = mapViewEnabled && d2DKnockEnabled

  const columnOrder = useSelector(selectOpportunityColumnOrder)
  const [columns, columnVisibility] = useMemo(
    () =>
      arrangeColumns(
        columnOrder,
        opportunityListColumns(allTargetPests, true, knockStatusEnabled),
        lockedOpportunityColumns,
        customFields,
      ),
    [allTargetPests, columnOrder, customFields, knockStatusEnabled],
  )
  const { syncNewColumnsWithPreferences } = useAddMissingColumns({
    columnOrder,
    columns,
    newlyAddedColumns: [
      'customerReferralLocationCode',
      'employeeReferral',
      'otherReferral',
      ...(knockStatusEnabled ? ['knockStatus', 'knockStatusReason'] : []),
    ],
    preference: 'opp',
  })

  const loading =
    divStatus === 'pending' ||
    locTypeStatus === 'pending' ||
    funnelStatus === 'pending' ||
    stageStatus === 'pending' ||
    teamStatus === 'pending' ||
    sourceStatus === 'pending' ||
    campStatus === 'pending' ||
    branchStatus === 'pending' ||
    targetPests === 'pending' ||
    userPrefsLoading ||
    customFieldsLoading

  useEffect(() => {
    syncNewColumnsWithPreferences()
  }, [columnOrder, syncNewColumnsWithPreferences])

  return { loading, columns, columnVisibility }
}
